// The datasets Shikshan Nivesh publishes, in one place. The home page, /data,
// each dataset page and their structured data all read this file, so a fact
// changes here once. Every value must be true of what greeksoup.ai shows today
// (checked 22 October 2026). No counts of companies or rows: the dataset is
// still growing (his ruling, 16 October 2026).

export const GREEKSOUP = "https://greeksoup.ai";
export const SITE = "https://shikshannivesh.com";

/** Covered companies, said the same way everywhere. */
export const COVERAGE =
  "US-listed companies in the S&P 500, S&P MidCap 400 and S&P SmallCap 600, and larger companies based outside the US that file with the SEC";

export interface Dataset {
  slug: string;
  /** Row name, as a reader says it. */
  name: string;
  /** The filing or record it comes from, in plain words. */
  source: string;
  /** What it holds, one sentence. */
  holds: string;
  /** Where the records start, as a plain fact (checked against the record store, 22 October 2026). */
  since: string;
  /** How often it updates. */
  updates: string;
  /** One live page on greeksoup.ai that shows it. */
  live: { label: string; url: string };
  /** On the home page's short table. */
  home: boolean;
  /** Its sample file on greeksoup.ai, /stocks/samples/<sample>.json (src/content/samples.ts). */
  sample?: string;
  /** Catalogue group, in the order the catalogue shows them. */
  group: "Financials" | "Company filings" | "Insiders and owners" | "Government" | "Market";
  /** Its card spans the whole row on /data and the home page (the newest dataset). */
  wide?: boolean;
  /** Tab of Apple's record that shows it, on the home page and /data. */
  appleTab?: string;
  /** Has its own page at /data/<slug>. */
  page?: DatasetPage;
}

export interface DatasetPage {
  /** Search title, search words first, under 60 characters. */
  searchTitle: string;
  description: string;
  h1: string;
  /** The answer an answer engine can lift whole. */
  lead: string;
  addedTitle?: string;
  added?: { type: string; meaning: string }[];
  addedNote?: string;
  /** What the filing link in each row is called: "Form 4", "Form 144". */
  filingLabel: string;
  /** The chart of the sample: value filed each day, trades each day, a change per company, or the stakes filed. */
  chart:
    | { title: string; kind: "daily"; note: string }
    | { title: string; kind: "dots"; note: string }
    | { title: string; kind: "change"; field: string; invert?: boolean; note: string }
    | { title: string; kind: "stake"; note: string };
  fields: { field: string; meaning: string }[];
  uses: string[];
  links: { label: string; url: string }[];
  faqs: { q: string; a: string }[];
}

export const DATASETS: Dataset[] = [
  {
    slug: "financial-statements",
    name: "Results and financial statements",
    source: "Each company's 10-K and 10-Q, and the 20-F, 40-F and 6-K of companies based outside the US",
    holds: "Income statement, balance sheet and cash flow for every quarter and year, with ratios and growth worked out the same way for every company.",
    since: "From FY2007; quarters from 2008",
    appleTab: "statements",
    updates: "Each time the company files",
    live: { label: "Microsoft on greeksoup.ai", url: `${GREEKSOUP}/stocks/us/msft/` },
    home: true,
    sample: "financial-statements",
    group: "Financials",
    page: {
      searchTitle: "US Company Financial Statements Data | Shikshan Nivesh",
      description:
        "Income statements, balance sheets and cash flows for US companies from each 10-K and 10-Q, by quarter and by fiscal year, with margins, growth and returns worked out the same way for every company.",
      h1: "US company financial statements from 10-K and 10-Q filings",
      lead:
        "Every US company reports its results to the SEC: a 10-Q each quarter and a 10-K for the full year. This dataset holds the income statement, balance sheet and cash flow from each of them, quarter by quarter and year by year, with margins, growth and returns worked out the same way for every company, so you can compare one company with another.",
      filingLabel: "Filing",
      chart: { title: "Revenue growth on the year, newest results", kind: "change", field: "revenue_change", note: "Each company's latest quarter against the same quarter a year earlier" },
      fields: [
        { field: "Period", meaning: "The fiscal quarter or year as the company names it, such as Q3 FY2026." },
        { field: "Reported", meaning: "The date the 10-Q or 10-K reached the SEC." },
        { field: "Income statement", meaning: "Revenue, cost of revenue, gross profit, R&D, SG&A, operating income, interest, pre-tax income, tax, net income and diluted EPS." },
        { field: "Balance sheet", meaning: "Cash, receivables, inventory, total assets, debt and equity." },
        { field: "Cash flow", meaning: "Operating cash flow, capital expenditure, free cash flow, buybacks and dividends paid." },
        { field: "Ratios", meaning: "Gross, operating and net margin, growth on the year, return on equity and on capital, and debt to equity." },
        { field: "Shares", meaning: "Diluted weighted shares and shares outstanding, adjusted for splits." },
        { field: "Filing", meaning: "The 10-Q or 10-K itself, on the SEC's site." },
      ],
      uses: [
        "An investor reading a company's quarters side by side before buying it.",
        "An analyst building a model from the lines the company itself reported.",
        "A screen that compares margins or growth across companies on the same basis.",
        "A writer who needs the numbers behind a results story the day they are filed.",
      ],
      links: [
        { label: "Apple's results on greeksoup.ai", url: `${GREEKSOUP}/stocks/us/aapl/` },
        { label: "Results this month", url: `${GREEKSOUP}/stocks/us/results/` },
      ],
      faqs: [
        {
          q: "Which filings does it come from?",
          a: "The 10-Q a company files each quarter and the 10-K it files for the full year, plus the 20-F, 40-F and 6-K of companies based outside the US that file with the SEC.",
        },
        {
          q: "How far back does it go?",
          a: "Fiscal years go back to FY2007 and quarters to 2008. Apple and JPMorgan each have 72 quarters on record. A company starts with its first filing on EDGAR, so a younger company has fewer years.",
        },
        {
          q: "Why can a figure differ from another site?",
          a: "Sites differ in how they treat one-off items and which lines they add together. Each figure here is the line the company filed, and every ratio is worked out from those lines the same way for every company.",
        },
        { q: "How quickly do new results show up?", a: "Within a day of the filing reaching the SEC." },
        { q: "Which companies are covered?", a: `${COVERAGE}. More companies are added as the dataset grows.` },
      ],
    },
  },
  {
    slug: "segments",
    name: "Revenue by segment",
    source: "Each company's 10-K, and the 20-F or 40-F of companies based outside the US",
    holds: "Revenue split by business, by product and by region, as each annual report gives it.",
    since: "From each company's annual reports",
    updates: "Each time the company files its annual report",
    live: { label: "Apple on greeksoup.ai", url: `${GREEKSOUP}/stocks/us/aapl/` },
    home: true,
    group: "Financials",
    appleTab: "segments",
  },
  {
    slug: "dividends",
    name: "Dividends",
    source: "Each company's 10-K, 10-Q and 8-K",
    holds: "Dividend per share by fiscal year and by quarter, the declared and payable dates, and the cash paid against profit and free cash flow.",
    since: "From FY2007",
    updates: "Each time the company files",
    live: { label: "Apple's dividends", url: `${GREEKSOUP}/stocks/us/aapl/dividends/` },
    home: true,
    group: "Financials",
    appleTab: "dividends",
  },
  {
    slug: "insider-trades",
    name: "Insider trades",
    source: "Form 4 filings with the SEC",
    holds: "Purchases and sales by officers, directors and owners of more than 10% of the shares, each sorted by what the trade really was.",
    since: "From October 2025",
    appleTab: "insiders",
    updates: "Every day",
    live: { label: "Apple's insider trades", url: `${GREEKSOUP}/stocks/us/aapl/insiders/` },
    home: true,
    sample: "insider-trades",
    group: "Insiders and owners",
    page: {
      searchTitle: "US Insider Trading Data (Form 4) | Shikshan Nivesh",
      description:
        "US insider trading data from Form 4 filings, read every day and sorted by what each trade was: open-market purchases, sales under a trading plan, shares taken as pay.",
      h1: "US insider trading data from Form 4 filings",
      lead:
        "Form 4 is the filing a company's officers, directors and owners of more than 10% of its shares send to the SEC when they buy or sell its stock, within two business days of the trade. Each trade in this dataset is sorted by what it actually was: a purchase with the insider's own money, a sale under a trading plan set months in advance, shares taken as pay, or shares the company awarded them.",
      addedTitle: "Every trade, sorted by what it was",
      added: [
        { type: "Purchase", meaning: "Bought on the open market with the insider's own money." },
        { type: "Plan purchase", meaning: "Bought through a company plan, such as dividends reinvested or an employee stock purchase plan." },
        { type: "Took pay in stock", meaning: "The insider was owed cash, such as fees or a salary, and took shares instead. The filing's footnote says so." },
        { type: "Sale", meaning: "Sold on the open market, outside any plan set in advance." },
        { type: "Sale, trading plan", meaning: "Sold under a Rule 10b5-1 plan set up months before, so the date was fixed in advance." },
      ],
      addedNote:
        "Grants, option exercises, shares held back for tax and gifts are in the data too, and they never count as insiders buying or selling.",
      filingLabel: "Form 4",
      chart: { title: "Insider sales and purchases, value filed each day", kind: "daily", note: "Every US company in the data, by the day the Form 4 was filed" },
      fields: [
        { field: "Traded", meaning: "The date of the trade." },
        { field: "Filed", meaning: "The date the Form 4 reached the SEC." },
        { field: "Insider", meaning: "The person who traded, as named on the filing." },
        { field: "Role", meaning: "Their position: chief executive, director, 10% owner and so on." },
        { field: "Type", meaning: "What the trade was, from the list above." },
        { field: "Shares, price, value", meaning: "How many shares, at what price, and the total in dollars." },
        { field: "Held after", meaning: "How many shares the insider owned once the trade was done." },
        { field: "Filing", meaning: "The Form 4 itself, on the SEC's site." },
      ],
      uses: [
        "An investor checking whether anyone inside a company they own has been buying.",
        "An analyst looking for senior insiders buying, or several insiders buying together.",
        "A writer covering a company's results who wants the insider side of the story.",
        "A screen, model or AI tool that needs each trade already sorted by type.",
      ],
      links: [
        { label: "Apple's insider trades", url: `${GREEKSOUP}/stocks/us/aapl/insiders/` },
        { label: "Senior insiders buying", url: `${GREEKSOUP}/stocks/screens/senior-insiders-buying/` },
        { label: "Insiders buying together", url: `${GREEKSOUP}/stocks/screens/insiders-buying-together/` },
      ],
      faqs: [
        {
          q: "What is a Form 4?",
          a: "A Form 4 is the filing an officer, a director or an owner of more than 10% of a company's shares sends to the SEC when they buy or sell its stock. It is due within two business days of the trade, and it shows the date, the number of shares, the price and how many shares the insider holds afterwards.",
        },
        {
          q: "What is a Rule 10b5-1 trading plan?",
          a: "A Rule 10b5-1 plan is set up in advance, at a time the insider holds no inside information, and it fixes when and how many shares will be sold or bought. Trades under it run on the plan's schedule, so the date of a planned sale says little about what the insider thought that day.",
        },
        {
          q: "Why are purchases and sales kept apart?",
          a: "Insiders sell for many reasons, such as taxes, a house or spreading out their wealth, and most sales run on plans set in advance. An open-market purchase is rarer: the insider spends their own money at the market price. Sorting each trade by type lets you see which one you are looking at.",
        },
        {
          q: "How quickly does a new Form 4 show up?",
          a: "Within a day of the Form 4 reaching the SEC.",
        },
        {
          q: "Which companies are covered?",
          a: `${COVERAGE}. More companies are added as the dataset grows.`,
        },
      ],
    },
  },
  {
    slug: "planned-insider-sales",
    name: "Planned insider sales",
    source: "Form 144 notices filed with the SEC",
    holds: "Notices an insider files before selling restricted or control shares, with the number of shares and their value.",
    since: "From October 2025",
    appleTab: "f144",
    updates: "Every day",
    live: { label: "Apple's planned sales", url: `${GREEKSOUP}/stocks/us/aapl/planned-sales/` },
    home: false,
    sample: "planned-insider-sales",
    group: "Insiders and owners",
    page: {
      searchTitle: "Form 144 Data: Planned Insider Sales | Shikshan Nivesh",
      description:
        "US Form 144 data: the notices insiders file before selling restricted or control shares, with the shares, their value and the Form 4 sale that followed, updated every day.",
      h1: "Planned insider sales from Form 144 filings",
      lead:
        "Before an officer, a director or a large owner sells restricted or control shares above the Rule 144 limits, they file a Form 144 with the SEC saying how many shares they plan to sell and through which broker. Each notice here comes with its value when filed and the Form 4 sale that followed, so you can see what was planned beside what was sold.",
      filingLabel: "Form 144",
      chart: { title: "Planned sales filed each day, by value", kind: "daily", note: "Every US company in the data, by the day the Form 144 was filed" },
      fields: [
        { field: "Filed", meaning: "The date the Form 144 reached the SEC." },
        { field: "Seller", meaning: "The person or trust planning to sell, as named on the notice." },
        { field: "Relationship", meaning: "Officer, director, 10% owner, or a former officer." },
        { field: "Shares, value", meaning: "How many shares may be sold, and their value on the notice." },
        { field: "Planned sale", meaning: "The date the seller expects to sell." },
        { field: "Broker", meaning: "The broker handling the sale." },
        { field: "How acquired", meaning: "How the seller got the shares: vested stock units, an option exercise, an open-market purchase and so on." },
        { field: "Sold, per Form 4", meaning: "The sale the insider reported afterwards on Form 4, where there is one." },
        { field: "Filing", meaning: "The Form 144 itself, on the SEC's site." },
      ],
      uses: [
        "An investor seeing an insider sale coming before it shows up on a Form 4.",
        "An analyst checking whether a planned sale was carried out in full.",
        "A screen for large planned sales by senior insiders.",
      ],
      links: [{ label: "Apple's planned sales", url: `${GREEKSOUP}/stocks/us/aapl/planned-sales/` }],
      faqs: [
        {
          q: "What is a Form 144?",
          a: "A Form 144 is the notice an insider files with the SEC before selling restricted or control shares above the Rule 144 limits. It names the shares, their value, the broker and the expected date of the sale.",
        },
        {
          q: "Does a Form 144 mean the shares were sold?",
          a: "It states an intention. The sale itself shows up later on the insider's Form 4, and sometimes fewer shares are sold than planned, or none at all.",
        },
        { q: "How quickly does a new notice show up?", a: "Within a day of the Form 144 reaching the SEC." },
        { q: "Which companies are covered?", a: `${COVERAGE}. More companies are added as the dataset grows.` },
      ],
    },
  },
  {
    slug: "fund-holdings-13f",
    name: "Fund holdings",
    source: "Form 13F reports filed with the SEC",
    holds: "What each institutional fund held at the end of every quarter, and what it added, trimmed or sold.",
    since: "From the September 2024 quarter",
    appleTab: "holders",
    updates: "Every day, as funds file",
    live: { label: "Berkshire Hathaway's 13F holdings", url: `${SITE}/data/funds/berkshire-hathaway-inc-1067983` },
    home: true,
    group: "Insiders and owners",
    page: {
      searchTitle: "13F Fund Holdings Data, Every Fund | Shikshan Nivesh",
      description:
        "Form 13F holdings for every fund that files one, each quarter since September 2024: what each fund holds, what it opened, added, trimmed and sold, and every fund holding a US company.",
      h1: "13F fund holdings, fund by fund",
      lead:
        "An investment manager with at least $100 million in US-listed shares files a Form 13F with the SEC within 45 days of each quarter's end, listing what it held on the quarter's last day. Each fund's report here sits beside its report for the quarter before, so you see what it opened, added to, trimmed and sold out of. Every company also has the list of every fund that holds it.",
      addedTitle: "Every holding, set beside the quarter before",
      added: [
        { type: "New position", meaning: "The fund holds the company now and did not a quarter ago." },
        { type: "Added", meaning: "The fund holds more shares than a quarter ago." },
        { type: "Trimmed", meaning: "The fund holds fewer shares than a quarter ago." },
        { type: "Sold out", meaning: "The fund held the company a quarter ago and holds none now." },
        { type: "Held", meaning: "The same number of shares as a quarter ago." },
      ],
      addedNote: "Where a stock split between the two quarters, the older share count is put on today's basis, so a split never reads as a purchase.",
      filingLabel: "13F",
      chart: { title: "Fund moves filed each day", kind: "daily", note: "Every fund's moves in the companies we cover, by the day its 13F was filed" },
      fields: [
        { field: "Fund", meaning: "The manager that filed the report, as named on its latest 13F." },
        { field: "Quarter", meaning: "The quarter end the holdings are for." },
        { field: "Filed", meaning: "The date the 13F reached the SEC." },
        { field: "Company", meaning: "The holding, with its ticker where it has one." },
        { field: "Shares, value", meaning: "Shares held at the quarter's end, and their value in dollars as the fund reported it." },
        { field: "Of the fund", meaning: "The holding's share of everything the fund reported." },
        { field: "Change", meaning: "Shares bought or sold since the quarter before." },
        { field: "Move", meaning: "New position, added, trimmed, sold out or held, from the list above." },
        { field: "Filing", meaning: "The 13F itself, on the SEC's site." },
      ],
      uses: [
        "An investor checking which funds bought a stock they own last quarter.",
        "An analyst following one manager's book, quarter by quarter.",
        "A screen for stocks that many funds opened in the same quarter.",
        "A model or AI tool that needs each fund's holdings with the change already worked out.",
      ],
      links: [
        { label: "Every fund that files a 13F", url: `${SITE}/data/funds` },
        { label: "Berkshire Hathaway's 13F holdings", url: `${SITE}/data/funds/berkshire-hathaway-inc-1067983` },
        { label: "Every fund holding Apple", url: `${SITE}/data/companies/aapl#funds` },
      ],
      faqs: [
        {
          q: "What is a Form 13F?",
          a: "The quarterly report an investment manager with at least $100 million in US-listed shares and other 13(f) securities files with the SEC. It lists each holding as it stood on the quarter's last day: the shares, their value, and any calls or puts.",
        },
        {
          q: "When do new reports show up?",
          a: "Funds have 45 days after each quarter ends, so the September 2026 quarter's reports are due by 16 November 2026, and many arrive in the last week. Each report shows up here within two days of reaching the SEC.",
        },
        { q: "How far back does it go?", a: "Every quarter from September 2024, and each new quarter is kept as it arrives." },
        {
          q: "Does a 13F show everything a fund owns?",
          a: "It shows long positions in US-listed shares and the other securities on the SEC's 13(f) list, on the quarter's last day. Short positions, cash, bonds and most non-US holdings are not on it, and trades inside the quarter do not show.",
        },
        { q: "Which funds and companies are covered?", a: `Every manager that files its own 13F, fund by fund. The moves table and each company's list of funds cover ${COVERAGE}.` },
      ],
    },
  },
  {
    slug: "large-holders",
    name: "Large holders",
    source: "Schedule 13D and 13G filings",
    holds: "Filings by anyone who owns more than 5% of a company's shares, with the stake they report.",
    since: "From October 2025",
    updates: "Every day",
    live: { label: "Apple's holders", url: `${GREEKSOUP}/stocks/us/aapl/holders/` },
    home: false,
    sample: "schedule-13d-13g",
    group: "Insiders and owners",
    page: {
      searchTitle: "Schedule 13D and 13G Data: 5% Holders | Shikshan Nivesh",
      description:
        "Schedule 13D and 13G filings for US companies: everyone who crosses 5% of a company's shares, the stake they report and the form they file, updated every day.",
      h1: "Large holders from Schedule 13D and 13G filings",
      lead:
        "Anyone who comes to own more than 5% of a US company's shares has to tell the SEC. A holder who may push the company to change, such as an activist, files a Schedule 13D, and a passive holder, such as an index fund, files a Schedule 13G. Each filing here comes with the holder, the company, the stake and the form.",
      filingLabel: "Filing",
      chart: { title: "Stakes reported in the newest filings", kind: "stake", note: "Each holder's stake as a share of the company's class of stock" },
      fields: [
        { field: "Filed", meaning: "The date the filing reached the SEC." },
        { field: "Holder", meaning: "The fund, firm or person reporting the stake." },
        { field: "Form", meaning: "Schedule 13D or 13G, with /A for an amendment to an earlier filing." },
        { field: "Stake", meaning: "The percentage of the class of shares the holder reports." },
        { field: "Company", meaning: "The company whose shares are held." },
        { field: "Filing", meaning: "The schedule itself, on the SEC's site." },
      ],
      uses: [
        "An investor seeing who has built a large stake in a company they own.",
        "An analyst watching for activists, who file a 13D.",
        "A screen for new 5% holders across many companies.",
      ],
      links: [{ label: "Apple's holders", url: `${GREEKSOUP}/stocks/us/aapl/holders/` }],
      faqs: [
        {
          q: "What is the difference between a 13D and a 13G?",
          a: "A 13D is filed by a holder who may try to influence the company, and it has to say what they plan to do. A 13G is the shorter form for passive holders, such as index funds, who do not plan to push for change.",
        },
        { q: "What does /A after the form mean?", a: "It is an amendment: the holder's stake or plans changed since their last filing." },
        { q: "How quickly does a new filing show up?", a: "Within a day of reaching the SEC." },
        { q: "Which companies are covered?", a: `${COVERAGE}. More companies are added as the dataset grows.` },
      ],
    },
  },
  {
    slug: "congress-trades",
    name: "Trades by members of Congress",
    source: "Periodic transaction reports of the House of Representatives",
    holds: "Stock trades members of the House disclose, with the amount range and the date they traded.",
    since: "Reports from January 2019",
    appleTab: "congress",
    updates: "Every day",
    live: { label: "Apple's Congress trades", url: `${GREEKSOUP}/stocks/us/aapl/congress/` },
    home: true,
    sample: "congress-trades",
    group: "Government",
    page: {
      searchTitle: "Congress Stock Trading Data (House PTRs) | Shikshan Nivesh",
      description:
        "Stock trades disclosed by members of the US House in periodic transaction reports: the member, the company, purchase or sale, the amount band and both dates, updated every day.",
      h1: "Stock trades by members of the US House",
      lead:
        "Members of the House have to report a stock trade in a periodic transaction report within 45 days. Each trade here comes with the member and their district, the company, whether it was a purchase or a sale, the amount band the report gives, and both the date of the trade and the date it was disclosed.",
      filingLabel: "Report",
      chart: { title: "Trades disclosed each day, purchases and sales", kind: "dots", note: "Every House trade in the data, by the day it was disclosed" },
      fields: [
        { field: "Disclosed", meaning: "The date the report was filed with the House." },
        { field: "Traded", meaning: "The date of the trade itself." },
        { field: "Member", meaning: "The member of the House, with their state and district." },
        { field: "Owner", meaning: "Whose account it was: the member's own, a spouse's, a joint account or a dependent child's." },
        { field: "Type", meaning: "Purchase, sale or partial sale." },
        { field: "Amount", meaning: "The band the report gives, such as $1,001 - $15,000." },
        { field: "Report", meaning: "The periodic transaction report itself, on the House's site." },
      ],
      uses: [
        "An investor checking whether members of Congress trade a stock they own.",
        "A writer covering what members of the House bought and sold this month.",
        "A screen for several members buying the same company.",
      ],
      links: [
        { label: "Every member's trades", url: `${SITE}/congress` },
        { label: "Apple's House trades", url: `${GREEKSOUP}/stocks/us/aapl/congress/` },
      ],
      faqs: [
        {
          q: "Why is the amount a band?",
          a: "The report gives a band, such as $1,001 to $15,000 or $100,001 to $250,000, and never the exact sum, so the band is what the data shows.",
        },
        {
          q: "Why are some trades disclosed weeks later?",
          a: "Members have up to 45 days to report a trade, so the disclosed date is often weeks after the trade date. Both dates are in every row.",
        },
        { q: "Are Senate trades included?", a: "The dataset holds House reports today." },
        { q: "Which companies are covered?", a: `${COVERAGE}. More companies are added as the dataset grows.` },
      ],
    },
  },
  {
    slug: "short-interest",
    name: "Short interest",
    source: "Short interest reported by brokers twice a month",
    holds: "Shares sold short and not yet bought back, and how many days of normal trading it would take to cover them.",
    since: "From December 2017",
    appleTab: "short",
    updates: "Twice a month",
    live: { label: "Apple's short interest", url: `${GREEKSOUP}/stocks/us/aapl/short-interest/` },
    home: true,
    sample: "short-interest",
    group: "Market",
    page: {
      searchTitle: "Short Interest Data for US Stocks | Shikshan Nivesh",
      description:
        "Short interest for US stocks at every twice-monthly settlement: shares sold short, the change on the period, average daily volume and days to cover.",
      h1: "Short interest for US stocks",
      lead:
        "Short interest is the number of shares investors have borrowed and sold, betting the price will fall, and not yet bought back. Brokers report it twice a month. Each settlement here comes with the shares short, the change on the settlement before, the average daily volume and the days to cover, which is how many days of normal trading it would take to buy them all back.",
      filingLabel: "Source",
      chart: { title: "Change in short interest, newest settlement", kind: "change", field: "change", invert: true, note: "A rise in shares short is drawn in red, a fall in green" },
      fields: [
        { field: "Settlement", meaning: "The settlement date the figure is for." },
        { field: "Shares short", meaning: "Shares sold short and not yet bought back on that date." },
        { field: "Change", meaning: "The change on the settlement before, in per cent." },
        { field: "Average daily volume", meaning: "The average number of shares traded each day over the period." },
        { field: "Days to cover", meaning: "Shares short divided by the average daily volume." },
        { field: "High and low", meaning: "The highest and lowest short interest of the twelve settlements before it." },
      ],
      uses: [
        "An investor checking how heavily a stock they own is shorted.",
        "An analyst watching days to cover climb ahead of results.",
        "A screen for the most shorted stocks.",
      ],
      links: [{ label: "Apple's short interest", url: `${GREEKSOUP}/stocks/us/aapl/short-interest/` }],
      faqs: [
        { q: "What is days to cover?", a: "Shares short divided by the average daily volume: how many days of normal trading it would take for short sellers to buy back every share." },
        { q: "How often does it update?", a: "Twice a month, about ten days after each settlement date." },
        { q: "Which companies are covered?", a: `${COVERAGE}. More companies are added as the dataset grows.` },
      ],
    },
  },
  {
    slug: "events",
    name: "Company events",
    source: "Each company's 8-K filings with the SEC",
    holds: "Every 8-K a company files, read as the event it reports: results, a leadership change, a material agreement, a deal completed, a shareholder vote and more.",
    since: "From August 2004, when the SEC numbered 8-K items",
    updates: "Every day",
    live: { label: "Apple's filings on greeksoup.ai", url: `${GREEKSOUP}/stocks/us/aapl/filings/` },
    home: false,
    sample: "events",
    group: "Company filings",
    wide: true,
    page: {
      searchTitle: "SEC 8-K Filings Data: US Company Events | Shikshan Nivesh",
      description:
        "Every 8-K a US company files, read as the event it reports: results, leadership changes, material agreements, deals completed and shareholder votes, with the moment the SEC accepted it.",
      h1: "US company events from 8-K filings",
      lead:
        "A company files an 8-K with the SEC within four business days of something its owners need to know: its results, a chief executive leaving, a large contract, a deal closing or the result of a shareholder vote. Each 8-K here is sorted by the event it reports, with the day it was filed and the moment the SEC accepted it, in New York time.",
      addedTitle: "Every 8-K, sorted by the event it reports",
      added: [
        { type: "Results", meaning: "Item 2.02: the company's results for a quarter or a year, usually with the press release." },
        { type: "Leadership change", meaning: "Item 5.02: a director or a senior officer leaving or joining, or a change to their pay." },
        { type: "Material agreement", meaning: "Item 1.01: a contract large enough to matter to the company's owners." },
        { type: "Deal completed", meaning: "Item 2.01: an acquisition or a sale of assets that has closed." },
        { type: "Shareholder vote", meaning: "Item 5.07: the results of a vote at a shareholder meeting." },
        { type: "Other events", meaning: "Item 8.01: anything else the company chooses to tell its owners." },
      ],
      addedNote: "An 8-K can report several items at once. The event shown first is the most telling one, and every item stays with the row. The rarer kinds, such as a restatement, an auditor change or a bankruptcy, are named the same way.",
      filingLabel: "8-K",
      chart: { title: "8-K events filed each day, by kind", kind: "daily", note: "Every US company in the data, the last 30 days" },
      fields: [
        { field: "Filed", meaning: "The date the 8-K reached the SEC." },
        { field: "Accepted by the SEC", meaning: "The moment the SEC accepted the filing, to the minute, in New York time. A dash means it is still being read." },
        { field: "Ticker", meaning: "The company's ticker." },
        { field: "Event", meaning: "The event the 8-K reports, the most telling first when it reports several." },
        { field: "Items", meaning: "The 8-K item numbers behind the event, such as 2.02 for results or 5.02 for a leadership change." },
        { field: "Form", meaning: "8-K, or 8-K/A when the company amends an earlier one." },
        { field: "Filing", meaning: "The 8-K itself, on the SEC's site." },
      ],
      uses: [
        "An investor who wants to know the day something changes at a company they own.",
        "An analyst following leadership changes, deals or votes across a sector.",
        "A writer covering a company the day it files its results or a large contract.",
        "A screen, model or AI tool that needs each 8-K already sorted by event.",
      ],
      links: [{ label: "Apple's filings on greeksoup.ai", url: `${GREEKSOUP}/stocks/us/aapl/filings/` }],
      faqs: [
        {
          q: "What is an 8-K?",
          a: "An 8-K is the filing a US company sends the SEC when something happens that its owners need to know about between its quarterly and annual reports: its results, a change at the top, a large contract, a deal or a shareholder vote.",
        },
        { q: "How quickly does a company have to file one?", a: "Within four business days of the event, for most items. Many companies file results the same day they announce them." },
        {
          q: "Why can one 8-K show several events?",
          a: "A single 8-K often reports more than one item, such as results together with a new director. The row shows the most telling event first and keeps every item number with it.",
        },
        {
          q: "What does accepted by the SEC mean?",
          a: "It is the moment the SEC's system took the filing in, read from the filing itself. It is shown in New York time, the time US markets trade on.",
        },
        { q: "Which companies are covered?", a: `${COVERAGE}. More companies are added as the dataset grows.` },
      ],
    },
  },
];

export const datasetBySlug = (slug?: string) => DATASETS.find((d) => d.slug === slug);

/** Who builds on the data, said the same way on the home page and /data. */
export const BUILDERS = [
  { who: "Investors", what: "checking the companies they own: results, who is buying, who is selling." },
  { who: "Analysts and research teams", what: "screening and modelling from the company's own reported lines, with ratios worked out the same way every time." },
  { who: "Newsletter and market writers", what: "covering a company's results with the insider, fund and Congress side beside them." },
  { who: "Builders of screens, models and AI tools", what: "who need clean company data that stays up to date." },
];

/** Structured data for a dataset (Google Dataset Search, answer engines). */
export const datasetJsonLd = (d: Dataset, url: string) => ({
  "@context": "https://schema.org",
  "@type": "Dataset",
  name: d.page ? d.page.h1 : `${d.name}, US companies`,
  description: d.page ? d.page.description : `${d.holds} From ${d.source}. ${d.since}. Updated: ${d.updates.toLowerCase()}.`,
  url,
  isAccessibleForFree: true,
  creator: { "@type": "Organization", "@id": `${SITE}/#organization`, name: "Shikshan Nivesh", url: SITE },
  spatialCoverage: "United States",
  isBasedOn: d.source,
  distribution: [{ "@type": "DataDownload", encodingFormat: "text/html", contentUrl: d.live.url }],
});
